import React from 'react';

// Card for `kind: "sensor.aggregation"` structured values.
//
// Expected fields:
//   minutes  — lookback window
//   key      — string or null (filter applied)
//   groups   — pre-formatted aggregation rows: { pico, key, unit, n,
//              min, max, avg, latest, latest_ts, station_count? }
//   series   — list of { pico, key, unit, points:[{ts, value}, …] }
//              (one entry per group; renders as inline SVG sparkline)
//
// Layout: heading, then per-(pico, key) row with the sparkline on the
// left, summary stats top-right, latest big-number bottom-right.
export default function SensorAggregationCard({ structured }) {
  const groups = structured.groups || [];
  const series = structured.series || [];
  const minutes = structured.minutes ?? 60;
  const filter = structured.key ? ` — ${structured.key}` : '';

  const seriesByKey = new Map(
    series.map((s) => [`${s.pico}:${s.key}`, s])
  );

  return (
    <div style={{
      marginBottom: '10px', padding: '12px', background: '#f8fafc',
      borderRadius: '8px', border: '1px solid #e2e8f0',
    }}>
      <div style={{
        fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px',
        color: '#334155',
      }}>
        Sensor activity (last {minutes} min{filter})
      </div>

      {groups.length === 0 && (
        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
          No data in window.
        </div>
      )}

      {groups.map((g, i) => {
        const unit = g.unit || '';
        const scope = g.station_count
          ? `${g.station_count} station${g.station_count === 1 ? '' : 's'}`
          : (g.station_id ? `s${g.station_id}` : '');
        const ser = seriesByKey.get(`${g.pico}:${g.key}`);
        return (
          <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: '120px 1fr auto',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 0',
            borderTop: i === 0 ? 'none' : '1px solid #e2e8f0',
          }}>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
                {g.pico}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                {g.key}{scope ? ` · ${scope}` : ''}
              </div>
            </div>

            <div>
              <Sparkline points={ser?.points || []} unit={unit} />
              <div style={{ fontSize: '0.7rem', color: '#475569',
                            display: 'flex', gap: '10px', flexWrap: 'wrap',
                            marginTop: '4px' }}>
                <span>min <b>{_fmt(g.min)}{unit}</b></span>
                <span>max <b>{_fmt(g.max)}{unit}</b></span>
                <span>avg <b>{_fmt(g.avg)}{unit}</b></span>
                <span>n=<b>{g.n}</b></span>
              </div>
            </div>

            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
              {_fmt(g.latest)}{unit}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Tiny SVG sparkline. Pure inline — no chart libraries. Renders a
// polyline scaled to the series' min/max with 10% vertical padding,
// plus a faint baseline at the average and dots on the first/last
// points.
//
// The SVG fills its container's width via `width="100%"` plus
// `preserveAspectRatio="none"`; the viewBox stays at the design
// dimensions so the polyline maths can use whole-number coordinates.
// Stroke / dot sizes get `vectorEffect="non-scaling-stroke"` so they
// don't deform when the box stretches horizontally.
function Sparkline({ points, unit }) {
  const W = 280, H = 48;
  if (!points || points.length === 0) {
    return (
      <div style={{ width: '100%', height: H, fontSize: '0.7rem',
                    color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
        (no points)
      </div>
    );
  }
  if (points.length === 1) {
    const p = points[0];
    return (
      <div style={{ width: '100%', height: H, fontSize: '0.75rem',
                    color: '#475569', display: 'flex', alignItems: 'center' }}>
        single sample · {_fmt(p.value)}{unit} @ {_shortTs(p.ts)}
      </div>
    );
  }

  const values = points.map((p) => p.value);
  const vMin = Math.min(...values);
  const vMax = Math.max(...values);
  const span = vMax - vMin || 1;
  const pad = span * 0.10;
  const yMin = vMin - pad;
  const yMax = vMax + pad;
  const yRange = yMax - yMin;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;

  const x = (i) => (points.length === 1 ? W / 2 : (i / (points.length - 1)) * W);
  const y = (v) => H - ((v - yMin) / yRange) * H;
  const polyPts = points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ');
  const yAvg = y(avg);

  const first = points[0];
  const last = points[points.length - 1];

  // y-range labels move out of the SVG (HTML-positioned) so the SVG
  // can stretch via preserveAspectRatio="none" without distorting the
  // text glyphs. Stats below the chart already cover min/max in absolute
  // form; these labels just anchor the axis edges visually.
  return (
    <div style={{ position: 'relative', width: '100%', height: H }}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}
           preserveAspectRatio="none"
           style={{ display: 'block' }}>
        <line x1="0" y1={yAvg} x2={W} y2={yAvg}
              stroke="#cbd5e1" strokeDasharray="2 3" strokeWidth="1"
              vectorEffect="non-scaling-stroke" />
        <polyline points={polyPts} fill="none" stroke="#0ea5e9"
                  strokeWidth="1.6" strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke" />
        <circle cx={x(0)} cy={y(first.value)} r="2.2" fill="#0ea5e9"
                vectorEffect="non-scaling-stroke" />
        <circle cx={x(points.length - 1)} cy={y(last.value)} r="2.2" fill="#0ea5e9"
                vectorEffect="non-scaling-stroke" />
      </svg>
      <span style={{
        position: 'absolute', top: 0, left: 2, fontSize: '0.65rem',
        color: '#94a3b8', pointerEvents: 'none',
      }}>{_fmt(yMax)}{unit}</span>
      <span style={{
        position: 'absolute', bottom: 0, left: 2, fontSize: '0.65rem',
        color: '#94a3b8', pointerEvents: 'none',
      }}>{_fmt(yMin)}{unit}</span>
    </div>
  );
}

function _fmt(v) {
  if (v == null) return '–';
  return typeof v === 'number' ? v.toFixed(1) : v;
}

function _shortTs(ts) {
  if (!ts) return '';
  const m = /T(\d{2}:\d{2})/.exec(ts);
  return m ? m[1] : ts;
}
